import dayjs from 'dayjs';
import dayjsIsSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter';
import dayjsIsSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';
import type { Hook } from '../types';

dayjs.extend(dayjsIsSameOrAfterPlugin);
dayjs.extend(dayjsIsSameOrBeforePlugin);

// 辅助函数
function createKLineContainer(container: HTMLElement): HTMLElement {
  const $klineContainer = document.createElement('div');
  $klineContainer.style.cssText = 'position: relative; width: 100%; height: 100%;';
  container.appendChild($klineContainer);
  return $klineContainer;
}

function determineTimePeriod(yearRange: number): string {
  if (yearRange > 5) {
    return 'month_1';
  }
  if (yearRange > 1) {
    return 'week_1';
  }
  return 'day_1';
}

function calculateDateRange(
  dates: string[],
  dateStartIndex?: number,
  dateEndIndex?: number
): [Date, Date] {
  const startDate = new Date(
    typeof dateStartIndex !== 'undefined' ? dates[dateStartIndex] : dates[0]
  );
  const endDate = new Date(
    typeof dateEndIndex !== 'undefined' ? dates[dateEndIndex] : dates[dates.length - 1]
  );
  return [startDate, endDate];
}

function updateAxisPointer(chartIns: unknown, date: string | null, dates: string[]) {
  const chartInsTemp = chartIns?.getECharts?.() || chartIns;

  if (!date) {
    chartInsTemp?.dispatchAction({ type: 'dvImmediatelyHideTip' });
    chartInsTemp?.dispatchAction({ type: 'updateAxisPointer' });
    return;
  }

  const dataIndex = dates.findIndex(date_ => dayjs(date_).isSameOrAfter(date));
  chartInsTemp.dispatchAction({ type: 'updateAxisPointer', seriesIndex: 0, dataIndex });
}

function updateDataZoom(chartIns: unknown, startDate: string, endDate: string, dates: string[]) {
  let startIndex = Math.max(
    0,
    dates.findIndex(date => dayjs(date).isSameOrAfter(dayjs(startDate))) - 1
  );
  let endIndex = Math.min(
    dates.length - 1,
    dates.findLastIndex(date => dayjs(date).isSameOrBefore(dayjs(endDate))) + 1
  );

  if (dayjs(dates[startIndex + 1]).isSame(dayjs(startDate))) {
    startIndex += 1;
  }
  if (dayjs(dates[endIndex - 1]).isSame(dayjs(endDate))) {
    endIndex -= 1;
  }

  (chartIns?.getECharts?.() || chartIns).dispatchAction({
    type: 'dataZoom',
    startValue: startIndex,
    endValue: endIndex
  });
}

export function addSubChartSimpleHXKLine(...args: Parameters<Hook['addSubChartSimpleHXKLine']>) {
  const [
    { layout, util: utils, hook: hooks, ui, token },
    {
      container,
      stocks,
      selectedStock,
      klineLegendIns,
      dates,
      dateStartIndex,
      dateEndIndex,
      xAxisIsDate,
      enableDataZoom,
      chartIns,
      enableTimeline,
      timelineIns,
      cardDom
    }
  ] = args;

  function calculateYearRange() {
    return (xAxisIsDate && !enableDataZoom) || enableTimeline
      ? Math.abs(new Date(dates[0]).getFullYear() - new Date(dates[dates.length - 1]).getFullYear())
      : 0;
  }

  let [startDate, endDate] = calculateDateRange(dates, dateStartIndex, dateEndIndex);
  const yearRange = calculateYearRange();
  const $klineContainer = createKLineContainer(container);
  const timePeriod = determineTimePeriod(yearRange);

  let selectedStockCode = selectedStock;
  let simpleHXKLine: InstanceType<(typeof ui)['SimpleHXKLine']>;
  let chartAxisPointerEventSilent = false;
  let chartDataZoomEventSilent = false;

  function createSimpleHXKLine(stockData) {
    return new ui.SimpleHXKLine({
      hxKLineVerifyInfo: hooks.globalConfig(args[0]).hxKLineVerifyInfo,
      container: $klineContainer,
      stockData,
      chartOption: {
        time_period: timePeriod,
        trade_date:
          Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())
            ? 0
            : [startDate, endDate]
      },
      layout: {
        currentLayout: layout,
        layoutInfo: utils.const.LAYOUT
      },
      cardDom,
      token
    });
  }

  function setupUpdateTooltip() {
    simpleHXKLine.on('updateTooltip', ({ date }) => {
      chartAxisPointerEventSilent = true;
      updateAxisPointer(chartIns, date, dates);
    });
  }

  function setupBrushing() {
    simpleHXKLine.on('brushing', ({ startDate: start, endDate: end }) => {
      chartDataZoomEventSilent = true;
      updateDataZoom(chartIns, start, end, dates);
    });
  }

  function setupSimpleHXKLineEvents() {
    if (xAxisIsDate) {
      setupUpdateTooltip();
      if (!enableTimeline) {
        setupBrushing();
      }
    }
  }

  function findStockData() {
    return stocks.find(item => String(item.code) === String(selectedStockCode));
  }

  function calculateZoomedDateRange(event) {
    const startIndex = Math.floor((event.start / 100) * (dates.length - 1));
    const endIndex = Math.ceil((event.end / 100) * (dates.length - 1));
    return [new Date(dates[startIndex]), new Date(dates[endIndex])];
  }

  function initSimpleHXKLine() {
    const stockData = findStockData();
    simpleHXKLine?.destroy();
    simpleHXKLine = createSimpleHXKLine(stockData);
    setupSimpleHXKLineEvents();
  }

  function setupLegendChangeEvent() {
    klineLegendIns?.on?.('change', event => {
      selectedStockCode = event.key;
      initSimpleHXKLine();
    });
  }

  function handleDataZoom(event) {
    if (chartDataZoomEventSilent) {
      chartDataZoomEventSilent = false;
      return;
    }
    [startDate, endDate] = calculateZoomedDateRange(event);
    initSimpleHXKLine();
  }

  function setupDataZoomEvent() {
    if (xAxisIsDate && enableDataZoom) {
      chartIns.on('datazoom', event => handleDataZoom(event));
    }
  }

  function setupTimelineChangeEvent() {
    if (enableTimeline) {
      timelineIns.on('change', ({ index }) => {
        simpleHXKLine.showTooltip({
          date: dates[index],
          triggerEvent: false,
          triggerAxisPointer: true
        });
      });
    }
  }

  function handleAxisPointerUpdate(dataIndex) {
    if (chartAxisPointerEventSilent || Number.isNaN(+dataIndex)) {
      chartAxisPointerEventSilent = false;
      return;
    }
    simpleHXKLine.showTooltip({
      date: dates[dataIndex],
      triggerEvent: false,
      triggerAxisPointer: true
    });
  }

  function setupChartMouseLeaveEvent() {
    const chartDom = chartIns?.getECharts()?.getDom() || chartIns?.getDom();
    chartDom?.addEventListener('mouseleave', () => {
      simpleHXKLine.showTooltip({ date: null, triggerEvent: false, triggerAxisPointer: true });
    });
  }

  function setupAxisPointerUpdateEvent() {
    if (xAxisIsDate) {
      chartIns.on('updateAxisPointer', ({ dataIndex }) => handleAxisPointerUpdate(dataIndex));
      setupChartMouseLeaveEvent();
    }
  }

  function setupEventListeners() {
    setupLegendChangeEvent();
    setupDataZoomEvent();
    setupTimelineChangeEvent();
    setupAxisPointerUpdateEvent();
  }

  setupEventListeners();
  initSimpleHXKLine();

  return {
    destroy: () => {
      simpleHXKLine.destroy();
    }
  } as ReturnType<Hook['addSubChartSimpleHXKLine']>;
}

export default addSubChartSimpleHXKLine;
